<script>
   /*************************************************************/
   /* AppContainer for training                                 */
   /* -------------------------                                 */
   /* app container for training of PCA model                   */
   /*************************************************************/

   import { downloadResults, downloadModel } from '../utils';

   import ModelCalculator from './ModelCalculator.svelte';
   import AppPlots from './AppPlots.svelte';

   import DataLoader from '../../../shared/components/PCADataLoader.svelte';
   import ResultTable from '../shared/ModelTable.svelte';

   import Outliers from '../../../shared/widgets/Outliers.svelte';
   import Container from '../../../shared/controls/Container.svelte';
   import Button from '../../../shared/controls/Button.svelte';


   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   // inputs
   export let WASM;              // WASM promis method from parent app


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // main properties of the training app
   // all set to undefined to avoid triggering
   // reset conditions on first render

   let dataset;               // calibration set
   let model;                 // object with PCA model
   let res;                   // object with PCA results for calibration set

   // parameters to check if model object has been updated
   let modelHash = 0;

   // array with outliers
   let outliers = [];         // vector with dataset row numbers (from 0) marked as outliers

   /**
    * Set the selected point as outlier and reset the model and results
    * @param e - event dispatched by AppPlots component with id of the selected point
    */
   function setOutlier(e) {

      // get position (index) of the sample to be removed
      const pos = parseInt(e.detail.id);
      if (Number.isNaN(pos) || pos < 0 || pos >= res.objindices.length ) return;

      // find sample ID corresponding to the position
      const id = res.objindices[pos];

      // filter the outlier out
      if (id >= 0 && !outliers.includes(id)) {
         outliers = [...outliers, id];
         model = null;
      }
   }


   /**
    * Reset outliers and model
    */
   function resetOutliers() {
      outliers = [];
      model = null;
   }


   $: if (dataset === null) {
      // if training set is set null - reset model and all related parameters
      model = null;
      outliers = [];
   }

   // if model object is set to null - reset dependent objects
   $: if (model === null) {
      res = null;
      modelHash = 0;
   }

   // check hashes and initiate updates if outcomes changed
   $: if (model && model.hash !== modelHash) {
      modelHash = model.hash;
   }
</script>

<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="data-loader">
         <DataLoader bind:dataset={dataset} className="Group"/>
      </div>
      {#if outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers {dataset} on:resetoutliers={resetOutliers} bind:outliers={outliers} />
      </div>
      {/if}
      {#if dataset }
      <div class="model-calculator">
         <ModelCalculator {WASM} {dataset} {outliers} bind:model={model} bind:res={res} />
         {#if res}
         <Container label='Download'>
            <div class="buttons">
            <Button on:click={() => downloadResults(res, dataset, "train", outliers, model)}>Results</Button>
            <Button on:click={() => downloadModel(model)}>Model</Button>
            </div>
         </Container>
         {/if}
      </div>
      {/if}
      <div class="pca-settings">
      </div>
      {#if res}
      <div class="result-stats">
         <ResultTable {res} />
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !res || !model}
         <div class="help-text">
         {#if !dataset}
            <p>
               In this tab you can train a PCA model and explore how well it describes the training set. After creating the model you can save it to a file and apply it to another, new set of samples, and see how well this new set is explained by the model (tab <strong>Project</strong>).
            </p>
            <p><strong>Step 1.</strong></p>
            <p>Load your training set from a CSV file. The file should contain data as numeric values delimited
               by comma (with dot as decimal separator) or by semicolon (with comma as decimal separator). Rows correspond to observations or objects, columns — to variables.
            </p>
            <p>
               First row in the CSV file can contain variable labels or values (<strong>header</strong>). It is assumed that variable lables are text (or have text parts), e.g. <em>"Na"</em>, <em>"OD260"</em>, etc. If variables are part of signals, e.g. spectra, the header can content numeric values, e.g. wavelength or wavenumbers. If this is the case, select <em>values</em> option for the header. In this case you will also be able to specify name of the values (e.g. <em>Wavelength</em>) and units (e.g. <em>nm</em>) if necessary.
            </p>
            <p>
               First column in the file can contain labels for the objects/observations (<strong>row labels</strong>) — just set the corresponding options after selecting the file. It is also possible to have group labels, which you can use, for example, to color group samples on the score plot. Here is an example how data structure may look like (note, that column with group, row labels and header are optional):
            </p>
            <div>
               <table>
               <tr>
                  <td>Name</td>
                  <td>Group</td>
                  <td>x1</td>
                  <td>x2</td>
                  <td>x3</td>
               </tr>
               <tr>
                  <td>O1</td>
                  <td>A</td>
                  <td>3.8</td>
                  <td>1.3</td>
                  <td>13.6</td>
               </tr>
               <tr>
                  <td>O2</td>
                  <td>A</td>
                  <td>11.1</td>
                  <td>12.3</td>
                  <td>3.3</td>
               </tr>
               <tr>
                  <td>O3</td>
                  <td>A</td>
                  <td>1.2</td>
                  <td>2.3</td>
                  <td>4.2</td>
               </tr>
               </table>
            </div>
            <p></p>
         {:else}
            {#if !model}
            <p><strong>Step 2</strong></p>
            <p>
               Next step is to create a global PCA model for the training set. Use more components/latent variables (PCs) than you expect to be optimal. You will have possibility to select number of components for every plot.
            </p>
            {/if}
         {/if}
         </div>
      {:else}
         {#key res }
         <AppPlots on:setoutlier={setOutlier} {dataset} {res} {model} />
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0 1em 0 0.5em;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      background: #e8e8e8;
      border-radius: 0.25rem;
   }

   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p, .help-text > div {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text table {
      border-collapse: collapse;
   }

   .help-text table td{
      padding: 0.25em 0.5em;
      background: #e0f0fa;
      text-align: right;
      border: 1px solid white;
   }

   .help-text table tr:first-child td{
      font-weight: bold;
      background: #f8f4e0;
   }

   .help-text table td:first-child {
      font-weight: bold;
      background: #f8f4e0;
      padding-right: 1em;
   }

   .help-text table td:nth-child(2) {
      background: #e0e8f0;
      padding-right: 1em;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }

   .result-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }

   .buttons {
      position: relative;
      box-sizing: border-box;
      display: inline-block;
      flex: 1 1 auto;
      margin: 0;
      padding: 2px 0;
      width: auto;
      border-radius: 2px;
      cursor: default;
      white-space: nowrap;
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(320px, 30%) 1fr;
      }
   }

</style>